<template>
<article class="product-card">
  <slot name="top"></slot>
  <h2 class="product-title">
    <slot name="title" v-bind="{ name: product.name }">
      {{ product.name }}
    </slot>
  </h2>
  <p v-if="!noDescription && product.description"
     class="product-short-description"
     v-html="product.description"
  ></p>

  <div v-if="hasCombinations" class="product-variants-wrapper">
    <div class="product-variants-list">
      <div v-for="combination in combinations" class="product-variant-selector">
        <label v-if="!noVariantLabels"
               :for="`product-${product.id}-combination-${combination.id}-value`"
        >
          {{ combination.name }}
        </label>
        <VueSelect class="product-variant-select"
                   v-model="choosen[combination.id]"
                   :reduce="o => o.id"
                   :options="combination.values"
                   :id="`product-${product.id}-combination-${combination.id}-value`"
                   label="name"
                   :clearable="false"
                   :searchable="false"
                   :get-option-key="o => o.id"
                   transition="fade"
        >
          <template #open-indicator="{ attributes }">
            <i class="fa fa-chevron-down" v-bind="attributes" />
          </template>
        </VueSelect>
      </div>
    </div>
  </div>

  <div v-if="hasModifiers" class="product-modifiers-wrapper">
    <div v-if="hasVisibleModifiersGroups" class="product-modifiers-groups-wrapper mt-4">
      <product-modifiers-group v-for="modifiersGroup in visibleModifiersGroups"
                               :key="`product-${product.id}-variant-${selectedVariant.id}-modifiers-group-${modifiersGroup.group.id}`"
                               :product-modifiers-group="modifiersGroup"
                               :preselected-modifiers="selectedVariant.selected_modifiers.modifiers || []"
                               v-model="modifiers[modifiersGroup.group.id]"
                               class="mb-4"
                               @initialized="enableModifiersWatcher"
      />
    </div>

    <div v-if="hasVisibleSingleModifiers" class="product-modifiers-wrapper mt-4">
      <product-modifier v-for="modifier in product.modifiers"
                        :key="`product-${product.id}-variant-${selectedVariant.id}-modifier-${modifier.modifier.id}`"
                        :product-modifier="modifier"
                        v-model="modifiers[modifier.modifier.id]"
                        class="mb-4"
      />
    </div>
  </div>

  <slot name="beforePriceAndButtons" :instance="this"/>

  <p class="text-sm mt-3" v-if="totalPrice.has_discount && !promotionId">
    <span class="line-through inline-block mr-2">{{ priceToText(totalPrice.original_price) }}</span>
    <span class="text-primary-darker">Выгода {{ priceToText(totalPrice.discount_difference) }}</span>
  </p>

  <div class="price-and-buttons" :class="{ 'has-discounted-price': totalPrice.has_discount && !promotionId, 'has-overridden-price': hasOverriddenPrice && !promotionId }">
    <h5 v-if="selectedVariant.price" class="product-price" :class="productPriceClasses">
      <template v-if="promotionId"><i class="fal fa-gift"></i> Подарок</template>
      <template v-else>{{ priceToText(totalPrice.price) }}</template>
      <i v-if="priceIsCalculating" class="fal fa-spinner fa-spin text-black text-sm"></i>
    </h5>

    <product-quantity-controls v-if="!(hideAllModifiers && hasRequiredModifiersGroups)"
                               class="quantity-controls"
                               :product="product"
                               :selected-variant="selectedVariant"
                               :is-in-stock="isInStock"
                               :modifiers="productModifiers"
                               :modifiers-hash="modifiersHash"
                               :cart-button-disabled="requiredModifiersAreMissing"
                               :promotion-id="promotionId"
                               :promotion-reward-id="rewardId"
    />
  </div>

  <slot name="bottom" :instance="this"></slot>

</article>
</template>

<script>
import VueSelect from 'vue-select';
import ProductVariants from '@/mixins/ProductVariants'
import ProductQuantityControls from './ProductQuantityControls'
import priceToText from '@/helpers/priceToText'
import getVariantWeight from '../getVariantWeight'
import ProductModifier from '@/components/products/partials/modifiers/ProductModifier'
import ProductModifiersGroup from '@/components/products/partials/modifiers/ProductModifiersGroup'
import axios from '@/zenky'
import { mapGetters } from 'vuex'

export default {
  props: {
    product: Object,

    category: Object,

    noDescription: {
      type: Boolean,
      default: false,
    },

    noVariantLabels: {
      type: Boolean,
      default: false,
    },

    promotionId: {
      required: false,
      type: String,
      default: null,
    },

    rewardId: {
      required: false,
      type: String,
      default: null,
    },

    source: {
      required: false,
      type: String,
      default: 'product_page',
    },
  },

  mixins: [ProductVariants],

  components: {
    VueSelect,
    ProductQuantityControls,
    ProductModifier,
    ProductModifiersGroup,
  },

  created() {
    this.generateCombinations()
  },

  mounted() {
    this.$nextTick(() => {
      if (!this.hasModifiers) {
        return;
      } else if (this.hasVisibleModifiersGroups) {
        return;
      } else if (!this.modifiersWatcherEnabled) {
        this.enableModifiersWatcher();
      }
    });
  },

  computed: {
    ...mapGetters({
      productsSettings: 'settings/products',
      cityId: 'store/activeCityId',
    }),

    hideAllModifiers() {
      if (this.source === 'product_page') {
        return false;
      }

      return this.productsSettings.modifiers_visibility === 'never';
    },

    displayOnlyRequiredModifiers() {
      if (this.source === 'product_page') {
        return false
      }

      return this.productsSettings.modifiers_visibility === 'only_required';
    },

    visibleModifiersGroups() {
      if (this.hideAllModifiers) {
        return [];
      } else if (!this.displayOnlyRequiredModifiers) {
        return this.product.modifiers_groups
      }

      return this.product.modifiers_groups.filter(group => group.is_required)
    },

    productPriceClasses() {
      return {
        'lowered': this.totalPrice.has_discount && !this.promotionId,
        'overridden': this.hasOverriddenPrice && !this.promotionId,
        'promotion-reward-price': !!this.promotionId,
      }
    },

    categoryId() {
      if (this.category && this.category.id) {
        return this.category.id
      } else if (this.product.categories && this.product.categories.length > 0) {
        return this.product.categories[0].id
      }

      return ''
    },

    variantWeight() {
      return getVariantWeight(this.selectedVariant);
    },

    hasDiscountedPrice() {
      return this.selectedVariant.has_discount === true
    },

    hasOverriddenPrice() {
      return this.overriddenPrice !== null
    },

    hasModifiers() {
      return this.hasVisibleSingleModifiers || this.hasVisibleModifiersGroups
    },

    hasRequiredModifiersGroups() {
      return this.product.modifiers_groups.some(group => group.is_required)
    },

    hasVisibleModifiersGroups() {
      return Array.isArray(this.visibleModifiersGroups) && this.visibleModifiersGroups.length > 0
    },

    hasVisibleSingleModifiers() {
      if (this.hideAllModifiers || this.displayOnlyRequiredModifiers) {
        return false
      }

      return this.hasSingleModifiers
    },

    hasSingleModifiers() {
      return Array.isArray(this.product.modifiers) && this.product.modifiers.length > 0
    },

    hasModifiersGroups() {
      return Array.isArray(this.product.modifiers_groups) && this.product.modifiers_groups.length > 0
    },

    totalPrice() {
      if (this.overriddenPrice !== null) {
        return this.overriddenPrice
      }

      return {
        price: this.selectedVariant.price,
        original_price: this.selectedVariant.original_price,
        has_discount: this.hasDiscountedPrice,
        discount_difference: this.selectedVariant.discount_difference,
        discount_percentage: this.selectedVariant.discount_percentage,
      }
    },

    productModifiers() {
      if (!this.modifiers || typeof this.modifiers !== 'object') {
        return []
      } else if (!Object.keys(this.modifiers).length) {
        return []
      }

      const modifiers = []

      for (let i in this.modifiers) {
        if (!this.modifiers.hasOwnProperty(i)) {
          continue
        }

        if (Array.isArray(this.modifiers[i])) {
          this.modifiers[i].forEach(modifier => {
            if (!modifier.modifier_id || !modifier.quantity) {
              return
            }

            modifiers.push(modifier)
          })

          continue
        } else if (!this.modifiers[i].quantity) {
          continue
        } else if (!this.modifiers[i].modifier_id) {
          continue
        }

        modifiers.push(this.modifiers[i])
      }

      return modifiers
    },

    requiredModifiersAreMissing() {
      if (!this.hasModifiers) {
        return false
      }

      let missing = 0

      this.product.modifiers.forEach(modifier => {
        if (!modifier.is_required) {
          return
        } else if (typeof this.modifiers[modifier.modifier.id] === 'undefined') {
          ++missing
        } else if (this.modifiers[modifier.modifier.id].quantity !== modifier.min_quantity) {
          ++missing
        }
      })

      if (missing > 0) {
        return true
      }

      this.product.modifiers_groups.forEach(group => {
        if (!group.is_required) {
          return
        } else if (typeof this.modifiers[group.group.id] === 'undefined') {
          ++missing
        } else if (!this.hasValidModifiers(this.modifiers[group.group.id])) {
          ++missing
        }
      })

      return missing > 0
    },
  },

  methods: {
    priceToText,

    hasValidModifiers(modifiers) {
      if (Array.isArray(modifiers)) {
        return modifiers.filter(modifier => !!modifier.modifier_id && modifier.quantity > 0).length > 0
      }
      if (typeof modifiers.quantity === 'undefined' || modifiers.quantity === 0) {
        return false
      } else if (typeof modifiers.modifier_id === 'undefined' || !modifiers.modifier_id) {
        return false
      }

      return true
    },

    recalculatePrice() {
      if (this.priceIsCalculating) {
        return
      }

      if (!Array.isArray(this.productModifiers) || !this.productModifiers.length) {
        this.overriddenPrice = null
        this.modifiersHash = null

        return
      }

      this.priceIsCalculating = true

      const url = `products/${this.product.id}/variants/${this.selectedVariant.id}/price?city_id=${this.cityId}`

      axios.post(url, { modifiers: this.productModifiers })
        .then(({ data }) => {
          this.overriddenPrice = data.data
          this.modifiersHash = data.data.modifiers_hash
          this.priceIsCalculating = false
        })
        .catch(err => {
          this.priceIsCalculating = false
        })
    },

    enableModifiersWatcher() {
      this.modifiersHash = this.selectedVariant.selected_modifiers.hash || null
      this.modifiersWatcherEnabled = true
    },
  },

  watch: {
    productModifiers() {
      if (this.modifiersWatcherEnabled) {
        this.recalculatePrice()
      }
    },

    selectedVariant() {
      this.$set(this, 'modifiers', {})
      this.overriddenPrice = null
      this.modifiersHash = null
    },
  },

  data() {
    return {
      combinations: [],
      choosen: {},
      modifiers: {},
      overriddenPrice: null,
      priceIsCalculating: false,
      modifiersHash: null,
      modifiersWatcherEnabled: false,
    }
  }
}
</script>

<style lang="less">
@import "~@/assets/less/components/utilities";

.product-card {
  @apply relative bg-white;
  transition: box-shadow .1s ease-in;

  .product-card-image {
    @apply relative;
    .badges {
      @apply absolute inset-0 flex flex-col items-start pointer-events-none;
      .badge {
        @apply pointer-events-auto bg-success-light mb-1 p-1 text-xs text-white rounded-sm;
      }
    }
  }

  /*
  .product-features {
    @apply opacity-80 font-light text-sm my-3;

    // A hack to achieve items having space between each other, but still spanning to the full width of the container.
    // https://stackoverflow.com/questions/31844421. We might have just added margin-right for spans without setting
    // the container type to flex and collapsing margin would to its thing, but flex prioritizes breaking between its
    // items, not within them.
    @apply w-full overflow-x-hidden; // So it doesn't affect other elements.
    .product-features-container {
      @apply flex flex-wrap -mr-5;
      span {
        @apply mr-5; // Same as negative parent margin
      }
    }
  }
  */

  .product-price-original {
    @apply text-xs;
  }
  .price-and-buttons {
    display: grid;
    grid-template-areas:
      "discount discount discount"
      "price fav cart-controls";
    grid-template-columns: auto min-content min-content;
    grid-gap: 0 0.5rem; // Can use margins if this isn't working out
    /* For not compact (tech) cards:
    grid-template-areas:
      "discount discount"
      "price price"
      "fav cart-controls";
    */
    .discount {
      grid-area: discount;
      .old-price {
        @apply line-through text-sm opacity-60;
      }
      .save {
        @apply text-primary text-xs align-middle ml-1;
      }
    }
    .product-price {
      @apply text-2xl font-normal self-center;
      grid-area: price;

      &.lowered {
        @apply text-primary;
      }

      &.overridden {
        @apply text-primary-darker;
      }

      &.promotion-reward-price {
        @apply text-lg text-primary-darker;
      }
    }
    .btn-favorite {
      @apply h-8 w-8 border border-grey text-grey flex justify-center;
      grid-area: fav;
      .fas {
        @apply font-light;
      }
    }
    .quantity-controls {
      @apply text-base w-auto h-8 w-24;
      grid-area: cart-controls;
      > * {
        @apply h-full;
      }
      .product-quantity-controls-wrapper {
        @apply bg-primary text-white;
        .btn {
          @apply h-full text-base text-white p-0;
          .fal {
            @apply flex justify-center; // To perfectly center the signs. TODO apply to all `.fal`s?
          }
        }
        input {
          @apply bg-primary text-white font-bold;
        }
      }
      .product-cart-controls-wrapper {
        .distorted-button {
          @apply w-full h-full p-0;
          .button__bg {
            @apply p-0;
          }
          // Adjust bubble effect positions with button width change.
          .circle {
            width: 15px;
            height: 15px;
            &.top-left {
              top: 46%;
              left: 31%;
            }
            &.bottom-right {
              bottom: 46%;
              right: 31%;
            }
          }
        }
        button {
          @apply text-sm font-medium w-full h-full;
        }
      }
    }
  }

  /*
  .product-remainders-wrapper {
    @apply text-center;

    p {
      @apply text-sm text-grey font-light;

      span {
        @apply text-black;
      }
    }
  }
  */
}

.product-variant-selector {
  label {
    @apply block font-light text-black text-sm opacity-80;

    &.font-normal {
      @apply font-normal;
    }
  }
  .v-select {
    @apply relative;
    .select-and-dropdown-items {
      @apply bg-grey-lightest px-2 py-1 leading-none font-light text-sm text-grey-darkest whitespace-no-wrap
        cursor-pointer;
    }
    &.vs--unsearchable {
      .vs__search {
        // It takes some space by default, so let's reduce it.
        @apply w-0 h-0 p-0 border-none absolute;
      }
    }
    .vs__dropdown-toggle {
      .select-and-dropdown-items();
      @apply rounded-sm flex justify-between;
      .vs__open-indicator {
        @apply text-primary text-2xs;
      }
    }
    &.vs--open {
      .vs__dropdown-toggle {
        @apply rounded-b-none;
      }
    }
    .vs__dropdown-menu {
      @apply absolute min-w-full z-10;
      .vs__dropdown-option {
        .select-and-dropdown-items();
      }
      &.fade-enter-active,
      &.fade-leave-active {
        transition-duration: 0.15s;
      }
    }
  }
}
</style>
